import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import apiClient, { extractMessage } from '@/services/apiClient'
import { onRepoStatus, onDbImportStart, onDbImportComplete, onDbImportError } from '@/websocket/wsClient'

export interface Repository {
  id:            string
  name:          string
  /** Absolute filesystem path to the repo root, provided by the PHP RepositoryService. */
  path?:         string
  isGit:         boolean
  currentBranch: string | null
}

export interface UserRepository {
  id:             number
  label:          string
  nasUrl:         string
  username:       string
  localPath:      string | null
  cloneStatus:    'pending' | 'cloning' | 'ready' | 'error'
  lastPulledAt:   string | null
  dbStatus:       'provisioning' | 'ready' | 'error' | null
  dbLastImportAt: string | null
}

export interface RepoDatabase {
  dbHost:       string
  dbPort:       number
  dbName:       string
  dbUsername:   string
  dbPassword:   string
  dbStatus:     string
  lastImportAt: string | null
}

export const useRepoStore = defineStore('repo', () => {
  const repositories  = ref<Repository[]>([])
  const activeRepo    = ref<Repository | null>(null)
  const loading       = ref(false)
  const error         = ref<string | null>(null)
  const userRepos     = ref<UserRepository[]>([])
  const currentUser      = ref<string>('')
  const currentUserId    = ref<number>(0)
  const currentUserEmail = ref<string>('')

  // DB Import-State: welches Repo gerade importiert wird + Fehlermeldungen
  const importingRepoId = ref<number | null>(null)
  const importErrors    = ref<Record<number, string>>({})

  const activeRepoRootPath = computed<string>(() => {
    const repo = activeRepo.value
    if (!repo) return ''
    if (repo.path?.startsWith('/')) return repo.path
    return repo.name ?? ''
  })

  const activeRepoPath = computed<string>(() => activeRepo.value?.path ?? activeRepo.value?.name ?? '')

  /** Fetch the repository list from the server. Auto-selects the first repo. */
  async function fetchRepositories(): Promise<void> {
    loading.value = true
    error.value   = null
    try {
      const { data } = await apiClient.get<{ repositories: Repository[] }>('/api/ide/repositories')
      repositories.value = data.repositories
      if (!activeRepo.value && repositories.value.length > 0) {
        activeRepo.value = repositories.value[0]
      }
    } catch (err) {
      error.value = extractMessage(err)
    } finally {
      loading.value = false
    }
  }

  function setActiveRepo(repo: Repository): void {
    activeRepo.value = repo
  }

  // ── User-owned NAS repositories ─────────────────────────────────────────────

  async function fetchUserRepos(): Promise<void> {
    try {
      const { data } = await apiClient.get<UserRepository[]>('/api/ide/user-repositories')
      userRepos.value = data
    } catch (err) {
      console.error('[repoStore] fetchUserRepos failed:', extractMessage(err))
    }
  }

  async function createRepo(payload: { label: string; nasUrl: string; username: string; password: string }): Promise<void> {
    const { data: repo } = await apiClient.post<UserRepository>('/api/ide/user-repositories/create', {
      label:    payload.label,
      nasUrl:   payload.nasUrl,
      username: payload.username,
      password: payload.password,
    })

    userRepos.value = [...userRepos.value, repo]
    // Clone wird jetzt von PHP server-seitig angestoßen — kein separater Node.js-Aufruf
  }

  /**
   * Repository löschen: PHP entfernt DB-Eintrag + MySQL-Datenbank,
   * danach bereinigt Node.js den lokalen Dateisystem-Clone.
   */
  async function deleteRepo(id: number): Promise<void> {
    const repo = userRepos.value.find(r => r.id === id)

    await apiClient.post(`/api/ide/user-repositories/${id}/delete`)
    userRepos.value = userRepos.value.filter(r => r.id !== id)

    // Filesystem-Cleanup über Node.js (best-effort)
    if (repo?.localPath) {
      axios.post('/node-api/internal/delete-repo',
        { localPath: repo.localPath },
        { headers: { Authorization: `Bearer ${window.__CRAFT_SESSION_TOKEN__ ?? ''}` } },
      ).catch(err => console.error('[repoStore] filesystem cleanup failed:', extractMessage(err)))
    }
  }

  async function pullRepo(id: number): Promise<void> {
    const { data: creds } = await apiClient.post<{
      nasUrl: string; username: string; password: string; localPath: string
    }>(`/api/ide/user-repositories/${id}/pull`)

    await axios.post('/node-api/internal/pull', {
      repoId:    id,
      nasUrl:    creds.nasUrl,
      username:  creds.username,
      password:  creds.password,
      localPath: creds.localPath,
    }, {
      headers: { Authorization: `Bearer ${window.__CRAFT_SESSION_TOKEN__ ?? ''}` },
    })
  }

  /** Fehlgeschlagenes DB-Provisioning neu starten. */
  async function retryDatabase(repoId: number): Promise<void> {
    userRepos.value = userRepos.value.map(r =>
      r.id === repoId ? { ...r, dbStatus: 'provisioning' } : r,
    )
    try {
      await apiClient.post(`/api/ide/user-repositories/${repoId}/retry-database`)
    } finally {
      await fetchUserRepos()
    }
  }

  /**
   * Decrypted NAS password for a user repo — used by the Git Panel to authenticate
   * SSH pull/push without requiring re-entry of credentials.
   */
  async function getNasPassword(repoId: number): Promise<string> {
    const { data } = await apiClient.post<{ nasUrl: string; username: string; password: string; localPath: string }>(
      `/api/ide/user-repositories/${repoId}/pull`,
    )
    return data.password
  }

  /** Datenbank-Zugangsdaten für ein Repository laden (inkl. Klartext-Passwort). */
  async function fetchDatabase(repoId: number): Promise<RepoDatabase> {
    const { data } = await apiClient.get<RepoDatabase>(`/api/ide/user-repositories/${repoId}/database`)
    return data
  }

  /**
   * SQL-Datei in die Repository-Datenbank importieren.
   * Holt zuerst die Credentials von PHP, sendet dann die Datei als
   * Octet-Stream an Node.js. Das Ergebnis kommt per WebSocket zurück.
   */
  async function importSql(repoId: number, file: File): Promise<void> {
    importingRepoId.value = repoId
    delete importErrors.value[repoId]

    // Credentials von PHP holen
    const creds = await fetchDatabase(repoId)

    // Datei als Raw-Stream an Node.js senden (kein Größenlimit durch Axios)
    await axios.post(`/node-api/internal/db-import/${repoId}`, file, {
      headers: {
        Authorization:   `Bearer ${window.__CRAFT_SESSION_TOKEN__ ?? ''}`,
        'Content-Type':  'application/octet-stream',
        'X-Db-Host':     creds.dbHost,
        'X-Db-Port':     String(creds.dbPort),
        'X-Db-Name':     creds.dbName,
        'X-Db-Username': creds.dbUsername,
        'X-Db-Password': creds.dbPassword,
      },
      timeout: 0, // kein Timeout für große Dateien
    })
    // Ergebnis (success/error) kommt via WebSocket db:import-complete / db:import-error
  }

  /**
   * WebSocket-Listener für Clone/Pull-Status-Events.
   * Aktualisiert den lokalen State und persistiert in PHP.
   */
  function initRepoStatusListener(): () => void {
    return onRepoStatus(async ({ repoId, status, localPath }) => {
      userRepos.value = userRepos.value.map(r =>
        r.id === repoId
          ? { ...r, cloneStatus: status, localPath: localPath ?? r.localPath }
          : r,
      )

      try {
        await apiClient.post('/api/ide/internal/repo-status', { repoId, status, localPath })
      } catch (err) {
        console.error('[repoStore] failed to persist repo status:', extractMessage(err))
      }
    })
  }

  /**
   * WebSocket-Listener für DB-Import-Events.
   * Bei Erfolg wird lastImportAt in PHP aktualisiert.
   */
  function initDbImportListener(): () => void {
    const offStart = onDbImportStart(({ repoId }) => {
      importingRepoId.value = repoId
    })

    const offComplete = onDbImportComplete(async ({ repoId }) => {
      importingRepoId.value = null
      userRepos.value = userRepos.value.map(r =>
        r.id === repoId ? { ...r, dbLastImportAt: new Date().toISOString() } : r,
      )
      try {
        await apiClient.post('/api/ide/internal/db-import-status', { repoId })
      } catch (err) {
        console.error('[repoStore] failed to persist import status:', extractMessage(err))
      }
    })

    const offError = onDbImportError(({ repoId, error: errMsg }) => {
      importingRepoId.value = null
      importErrors.value = { ...importErrors.value, [repoId]: errMsg }
      console.error(`[repoStore] db import error for repo ${repoId}:`, errMsg)
    })

    return () => { offStart(); offComplete(); offError() }
  }

  function activateRepo(repo: UserRepository): void {
    setActiveRepo({
      id:            String(repo.id),
      name:          repo.label,
      path:          repo.localPath ?? undefined,
      isGit:         true,
      currentBranch: null,
    })
  }

  return {
    repositories,
    activeRepo,
    activeRepoRootPath,
    activeRepoPath,
    userRepos,
    currentUser,
    currentUserId,
    currentUserEmail,
    loading,
    error,
    importingRepoId,
    importErrors,
    fetchRepositories,
    setActiveRepo,
    fetchUserRepos,
    createRepo,
    deleteRepo,
    pullRepo,
    getNasPassword,
    retryDatabase,
    fetchDatabase,
    importSql,
    activateRepo,
    initRepoStatusListener,
    initDbImportListener,
  }
})
